import { useEffect, useState } from 'react';
import { FaCopy, FaEnvelope, FaLink, FaUserShield } from 'react-icons/fa';
import workspaceAPI from '../../services/workspaceAPI';

const INVITE_VALIDITY_OPTIONS = [
  { value: '30m', label: '30 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '6h', label: '6 hours' },
  { value: '12h', label: '12 hours' },
  { value: '1d', label: '1 day' },
  { value: '3d', label: '3 days' },
  { value: '7d', label: '7 days' },
  { value: '15d', label: '15 days' },
  { value: '30d', label: '30 days' },
];

const WorkspaceStaff = () => {
  const [staff, setStaff] = useState([]);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [authorizeEmail, setAuthorizeEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('company_staff');
  const [authorizeRole, setAuthorizeRole] = useState('company_staff');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [inviteCodeValidTill, setInviteCodeValidTill] = useState('');
  const [showValidityOptions, setShowValidityOptions] = useState(false);
  const [validityMode, setValidityMode] = useState('preset');
  const [validityPreset, setValidityPreset] = useState('7d');
  const [validityCustomUntil, setValidityCustomUntil] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [staffRes, codeRes, linkRes] = await Promise.all([
        workspaceAPI.getStaff(),
        workspaceAPI.getInviteCode(),
        workspaceAPI.getStaffInviteLink(),
      ]);
      setStaff(staffRes.data.staff || []);
      setInviteCode(codeRes.data.inviteCode || '');
      setInviteCodeValidTill(codeRes.data.validTill || '');
      setInviteLink(linkRes.data.inviteUrl || '');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage(`${label} copied`);
    } catch {
      setError('Copy failed');
    }
  };

  const copyPersonalLink = async (email) => {
    try {
      const res = await workspaceAPI.getStaffInviteLink(email);
      await navigator.clipboard.writeText(res.data.inviteUrl);
      setMessage(`Personal invite link copied for ${email}`);
    } catch {
      setError('Failed to get personal link');
    }
  };

  const sendInviteLink = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return setError('Enter staff email');
    setSending(true);
    setError('');
    try {
      const res = await workspaceAPI.sendStaffInviteLink({ email: inviteEmail.trim(), role: inviteRole });
      setMessage(res.data.message);
      setInviteLink(res.data.inviteUrl || inviteLink);
      setInviteEmail('');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  const authorizeOnly = async (e) => {
    e.preventDefault();
    if (!authorizeEmail.trim()) return setError('Enter email to authorize');
    setError('');
    try {
      await workspaceAPI.inviteStaff({ email: authorizeEmail.trim(), role: authorizeRole });
      setMessage('Email authorized. Share invite code or personal link with staff.');
      setAuthorizeEmail('');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to authorize');
    }
  };

  if (loading) return <p className="workspace-loading">Loading staff...</p>;

  return (
    <div>
      <h1 className="workspace-page-title">Staff authorization management</h1>
      <p className="workspace-page-subtitle">
        Invite members with unified code, send email links, and control authorized login access.
      </p>
      {error && <p className="workspace-alert error">{error}</p>}
      {message && <p className="workspace-alert success">{message}</p>}

      <div className="workspace-staff-grid">
        <div className="workspace-invite-box">
          <h3>Unified invite code</h3>
          <p className="workspace-hint">One code for all staff — use with Join with code at login</p>
          <div className="workspace-invite-code">{inviteCode || '—'}</div>
          <p className="workspace-hint workspace-invite-validity-note">
            {inviteCodeValidTill
              ? `Code valid till: ${new Date(inviteCodeValidTill).toLocaleString()}`
              : 'Code validity: no expiry'}
          </p>
          <button
            type="button"
            className="workspace-btn secondary workspace-validity-toggle"
            onClick={() => setShowValidityOptions((prev) => !prev)}
          >
            Code valid till
          </button>
          {showValidityOptions && (
            <div className="workspace-invite-validity-config">
              <label className="workspace-validity-mode">
                <input
                  type="radio"
                  name="invite-validity-mode-staff"
                  value="preset"
                  checked={validityMode === 'preset'}
                  onChange={() => setValidityMode('preset')}
                />
                Preset duration
              </label>
              <label className="workspace-validity-mode">
                <input
                  type="radio"
                  name="invite-validity-mode-staff"
                  value="custom"
                  checked={validityMode === 'custom'}
                  onChange={() => setValidityMode('custom')}
                />
                Custom date & time
              </label>
              <label className="workspace-validity-mode">
                <input
                  type="radio"
                  name="invite-validity-mode-staff"
                  value="never"
                  checked={validityMode === 'never'}
                  onChange={() => setValidityMode('never')}
                />
                No expiry
              </label>
              {validityMode === 'preset' && (
                <select
                  className="workspace-input"
                  value={validityPreset}
                  onChange={(e) => setValidityPreset(e.target.value)}
                >
                  {INVITE_VALIDITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
              {validityMode === 'custom' && (
                <input
                  type="datetime-local"
                  className="workspace-input"
                  value={validityCustomUntil}
                  onChange={(e) => setValidityCustomUntil(e.target.value)}
                />
              )}
            </div>
          )}
          <div className="workspace-btn-row">
            <button type="button" className="workspace-btn secondary" onClick={() => copy(inviteCode, 'Code')}>
              <FaCopy /> Copy code
            </button>
            <button
              type="button"
              className="workspace-btn secondary"
              onClick={async () => {
                try {
                  const payload = {
                    validity: !showValidityOptions
                      ? { mode: 'never' }
                      : validityMode === 'preset'
                        ? { mode: 'preset', preset: validityPreset }
                        : validityMode === 'custom'
                          ? { mode: 'custom', customUntil: validityCustomUntil }
                          : { mode: 'never' },
                  };
                  const r = await workspaceAPI.regenerateInviteCode(payload);
                  setInviteCode(r.data.inviteCode);
                  setInviteCodeValidTill(r.data.validTill || '');
                  const lr = await workspaceAPI.getStaffInviteLink();
                  setInviteLink(lr.data.inviteUrl);
                  setMessage('Invite code regenerated');
                } catch (err) {
                  setError(err.response?.data?.message || 'Failed to regenerate invite code');
                }
              }}
            >
              Regenerate
            </button>
          </div>
        </div>
        <div className="workspace-invite-box">
          <h3>Generic invite link</h3>
          <p className="workspace-hint">Share via WhatsApp or any channel</p>
          <input className="workspace-input workspace-link-input" readOnly value={inviteLink} />
          <button type="button" className="workspace-btn secondary" onClick={() => copy(inviteLink, 'Link')}>
            <FaLink /> Copy link
          </button>
        </div>
      </div>

      <form onSubmit={sendInviteLink} className="workspace-card workspace-send-invite-card" style={{ marginBottom: 16 }}>
        <h3>
          <FaEnvelope /> Send invite link by email
        </h3>
        <p className="workspace-hint">Authorizes email + sends personal join link (requires SMTP in server .env)</p>
        <div className="workspace-form-row">
          <input
            type="email"
            className="workspace-input"
            placeholder="staff@company.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
          />
          <select className="workspace-input" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
            <option value="company_staff">Staff</option>
            <option value="company_manager">Manager</option>
          </select>
          <button type="submit" className="workspace-btn" disabled={sending}>
            {sending ? 'Sending...' : 'Send invite link'}
          </button>
        </div>
      </form>

      <form onSubmit={authorizeOnly} className="workspace-card" style={{ marginBottom: 20 }}>
        <h3>
          <FaUserShield /> Authorize email only (no email sent)
        </h3>
        <p className="workspace-hint">Staff can join manually with workspace code + this email</p>
        <div className="workspace-form-row">
          <input
            type="email"
            className="workspace-input"
            placeholder="staff@company.com"
            value={authorizeEmail}
            onChange={(e) => setAuthorizeEmail(e.target.value)}
            required
          />
          <select className="workspace-input" value={authorizeRole} onChange={(e) => setAuthorizeRole(e.target.value)}>
            <option value="company_staff">Staff</option>
            <option value="company_manager">Manager</option>
          </select>
          <button type="submit" className="workspace-btn secondary">
            Authorize
          </button>
        </div>
      </form>

      <div className="workspace-card workspace-login-steps" style={{ marginBottom: 20 }}>
        <h3>How authorized staff log in</h3>
        <ol className="workspace-steps-list">
          <li>You authorize their work email (above)</li>
          <li>They open the invite link or go to <strong>/join-workspace</strong></li>
          <li>They set a password using workspace code + email</li>
          <li>After setup: <strong>Company login</strong> with email + password</li>
          <li>Deactivated staff cannot sign in even with correct password</li>
        </ol>
      </div>

      <div className="workspace-table-wrap">
        <table className="workspace-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Login status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.map((m) => (
              <tr key={m.id}>
                <td>{m.name}</td>
                <td>{m.email}</td>
                <td>
                  {m.role === 'company_admin' ? (
                    'Admin'
                  ) : (
                    <select
                      className="workspace-input"
                      value={m.role}
                      onChange={(e) => workspaceAPI.updateStaff(m.id, { role: e.target.value }).then(load)}
                      style={{ minWidth: 120 }}
                    >
                      <option value="company_manager">Manager</option>
                      <option value="company_staff">Staff</option>
                    </select>
                  )}
                </td>
                <td>
                  <span
                    className={`workspace-badge ${
                      m.loginReady && m.isActive ? 'ok' : m.isActive ? 'pending' : 'off'
                    }`}
                  >
                    {m.loginReady ? 'Can log in' : m.isActive ? 'Awaiting password' : 'Deactivated'}
                  </span>
                </td>
                <td>
                  {m.role !== 'company_admin' && (
                    <div className="workspace-btn-row" style={{ margin: 0 }}>
                      <button
                        type="button"
                        className="workspace-btn secondary"
                        onClick={() =>
                          workspaceAPI.updateStaff(m.id, { isActive: !m.isActive }).then(load)
                        }
                      >
                        {m.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      {m.isActive && (
                        <button
                          type="button"
                          className="workspace-btn secondary"
                          onClick={() => copyPersonalLink(m.email)}
                        >
                          Copy link
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {staff.length === 0 && <p style={{ padding: 16 }}>No staff yet. Send an invite above.</p>}
      </div>
    </div>
  );
};

export default WorkspaceStaff;
